import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";

import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import DatePickerInput from "../../forms/input/DatePickerInput";
import { createPaymentV2, BillV2 } from "../../utils/billingServiceV2";
import StatusModal from "../../components/modals/StatusModal";

interface BillPaymentModalProps {
  open: boolean;
  bill: BillV2 | null;
  onClose: () => void;
  onSuccess: () => void;
}

const BillPaymentModal: React.FC<BillPaymentModalProps> = ({
  open,
  bill,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: 0,
    paymentMethod: "cash",
    paymentDate: new Date().toISOString(),
    paymentReference: "",
  });

  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    type: "success" | "error";
    message: string;
    title: string;
  }>({
    open: false,
    type: "success",
    message: "",
    title: "",
  });

  useEffect(() => {
    if (bill && open) {
      setFormData({
        amount: bill.netAmount ? bill.netAmount * -1 : 0,
        paymentMethod: "cash",
        paymentDate: new Date().toISOString(),
        paymentReference: `Payment for ${bill.billNumber}`,
      });
    }
  }, [bill, open]);

  const handlePayment = async () => {
    if (!bill) return;

    if (formData.amount <= 0) {
      alert("Amount must be greater than 0");
      return;
    }

    setLoading(true);
    try {
      const res = await createPaymentV2({
        customerId: bill.customerId,
        contractId: bill.contractId,
        billId: bill.id,
        amount: formData.amount,
        paymentMethod: formData.paymentMethod,
        paymentDate: formData.paymentDate,
        paymentReference: formData.paymentReference,
      });

      if (res && res.success) {
        setStatusModal({
          open: true,
          type: "success",
          title: "Payment Successful",
          message: "Payment recorded successfully",
        });
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        throw res;
      }
    } catch (err: any) {
      console.error("Error recording payment", err);
      setStatusModal({
        open: true,
        type: "error",
        title: "Payment Failed",
        message: err.message || "Failed to record payment",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Record Bill Payment</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: "8px" }}>
              <Typography variant="subtitle2" color="textSecondary">
                Bill Number
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {bill?.billNumber}
              </Typography>
              <Stack direction="row" spacing={4} mt={1}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Customer
                  </Typography>
                  <Typography variant="body2">{bill?.customerName}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Net Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{bill?.netAmount != null ? (bill.netAmount * -1).toLocaleString() : ""}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <TextField
              label="Amount Paid"
              type="number"
              fullWidth
              value={formData.amount}
              onChange={(e) =>
                setFormData({ ...formData, amount: Number(e.target.value) })
              }
              variant="outlined"
              size="small"
              required
            />

            <CustomOutlinedSelect
              label="Payment Method"
              options={[
                { label: "Cash", value: "cash" },
                { label: "Bank Transfer", value: "bank_transfer" },
                { label: "Credit Card", value: "credit_card" },
                { label: "Cheque", value: "cheque" },
                { label: "Wallet", value: "wallet" },
              ]}
              value={formData.paymentMethod}
              onChange={(opt) =>
                setFormData({ ...formData, paymentMethod: opt.value as string })
              }
            />

            <DatePickerInput
              label="Payment Date"
              value={formData.paymentDate}
              onChange={(date) =>
                setFormData({
                  ...formData,
                  paymentDate: date
                    ? new Date(
                        date.getTime() - date.getTimezoneOffset() * 60000,
                      ).toISOString()
                    : "",
                })
              }
              required
            />

            <TextField
              label="Payment Reference"
              fullWidth
              multiline
              rows={2}
              value={formData.paymentReference}
              onChange={(e) =>
                setFormData({ ...formData, paymentReference: e.target.value })
              }
              variant="outlined"
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            color="inherit"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayment}
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: "8px", px: 4 }}
          >
            {loading ? "Recording..." : "Record Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <StatusModal
        open={statusModal.open}
        type={statusModal.type}
        title={statusModal.title}
        message={statusModal.message}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
      />
    </>
  );
};

export default BillPaymentModal;
